import { useCallback, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Mic, Loader2, Radio } from "lucide-react";
import { RealtimeMeeting } from "@/components/live/RealtimeMeeting";

export default function CoreVoiceRoom() {
  const [authToken, setAuthToken] = useState(null);
  const [joining, setJoining] = useState(false);

  const join = useCallback(async () => {
    setJoining(true);
    try {
      const r = await api.post("/realtime/voice/token");
      setAuthToken(r.data.auth_token);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not join the voice room");
    } finally {
      setJoining(false);
    }
  }, []);

  const leave = useCallback(() => setAuthToken(null), []);

  if (authToken) {
    return (
      <div className="space-y-4" data-testid="voice-room-active">
        <div className="flex items-center gap-2">
          <Radio className="h-5 w-5 text-primary" />
          <h1 className="text-xl font-bold tracking-tight">Core Voice Room</h1>
        </div>
        <RealtimeMeeting
          authToken={authToken}
          defaults={{ audio: true, video: false }}
          onLeave={leave}
          className="h-[calc(100vh-260px)] min-h-[440px] w-full"
        />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="voice-room-lobby">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Core Voice Room</h1>
        <p className="mt-1 text-muted-foreground">
          A private, always-on audio room for the leadership team. Drop in any time to talk — no scheduling needed.
        </p>
      </div>
      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Mic className="h-5 w-5 text-primary" /> Join the conversation
          </CardTitle>
          <CardDescription>
            Open mic, audio only. You&apos;ll be asked to allow microphone access.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={join} disabled={joining} data-testid="voice-room-join-btn">
            {joining ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mic className="mr-2 h-4 w-4" />}
            Join voice room
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
